import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle, XCircle, Edit, Clock, FileText, Key, Copy, Trash2, Search, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterUazAPIContent = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, connected, disconnected, not_configured
  
  // Modal de configuração
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [instanceToken, setInstanceToken] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Modal de teste de mensagem
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('🧪 Mensagem de teste do Pede é Fácil!');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Configuração global
  const [globalConfig, setGlobalConfig] = useState(null);

  useEffect(() => {
    fetchRestaurants();
    fetchGlobalConfig();
  }, []);

  const fetchGlobalConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/evolution-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config global:', error);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/uazapi/restaurants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRestaurants(response.data.restaurants || []);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
      toast.error('Erro ao carregar lista de lojas');
      setLoading(false);
    }
  };

  const handleOpenConfig = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setInstanceToken('');
    setInstanceName(restaurant.instance_name || '');
    setTestResult(null);
    setShowConfigModal(true);
  };

  const handleTestConnection = async () => {
    if (!instanceToken.trim()) {
      toast.error('Digite o token da instância');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/uazapi/test-connection`, {
        instance_token: instanceToken.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success('Conexão válida!');
      } else {
        toast.error(response.data.message || 'Falha na conexão');
      }
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      setTestResult({ success: false, message: 'Erro ao testar conexão' });
      toast.error('Erro ao testar conexão');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!instanceToken.trim()) {
      toast.error('Digite o token da instância');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/uazapi/link-instance`, {
        restaurant_id: selectedRestaurant.id,
        instance_token: instanceToken.trim(),
        instance_name: instanceName.trim() || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(response.data.message || 'Instância vinculada com sucesso!');
      setShowConfigModal(false);
      fetchRestaurants();
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (restaurant) => {
    if (!window.confirm(`Deseja realmente desvincular a instância UazAPI da loja "${restaurant.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/master/uazapi/unlink-instance/${restaurant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Instância desvinculada com sucesso!');
      fetchRestaurants();
    } catch (error) {
      console.error('Erro ao desvincular:', error);
      toast.error('Erro ao desvincular instância');
    }
  };

  const handleOpenTestMessage = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setTestPhone('');
    setTestMessage('🧪 Mensagem de teste do Pede é Fácil!\n\nSe você recebeu esta mensagem, a integração UazAPI está funcionando! ✅');
    setShowTestModal(true);
  };

  const handleSendTestMessage = async () => {
    if (!testPhone.trim()) {
      toast.error('Digite o número de WhatsApp');
      return;
    }

    setSendingTest(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/master/uazapi/send-test-message`, {
        restaurant_id: selectedRestaurant.id,
        phone: testPhone.trim(),
        message: testMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Mensagem enviada com sucesso!');
        setShowTestModal(false);
      } else {
        toast.error(response.data.message || 'Falha ao enviar mensagem');
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem');
    } finally {
      setSendingTest(false);
    }
  };

  // Filtrar restaurantes
  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          r.subdomain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          r.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' ||
                          (filterStatus === 'connected' && r.whatsapp_status === 'connected') ||
                          (filterStatus === 'disconnected' && r.whatsapp_status === 'disconnected') ||
                          (filterStatus === 'not_configured' && !r.has_uazapi_token);
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (restaurant) => {
    if (!restaurant.has_uazapi_token) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">
          Não configurado
        </span>
      );
    }
    
    switch (restaurant.whatsapp_status) {
      case 'connected':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30 flex items-center gap-1">
            <CheckCircle size={12} /> Conectado
          </span>
        );
      case 'disconnected':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-1">
            <XCircle size={12} /> Desconectado
          </span>
        );
      case 'qrcode':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 flex items-center gap-1">
            <Clock size={12} /> Aguardando QR
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">
            {restaurant.whatsapp_status || 'Desconhecido'}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Verificar se UazAPI está configurado globalmente
  if (globalConfig && globalConfig.api_type !== 'uazapi') {
    return (
      <div className="p-6">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 text-center">
          <AlertTriangle className="mx-auto mb-4 text-yellow-400" size={48} />
          <h3 className="text-xl font-bold text-yellow-400 mb-2">UazAPI não está ativo</h3>
          <p className="text-gray-400 mb-4">
            Para gerenciar instâncias UazAPI por loja, primeiro configure o UazAPI como API ativa nas Configurações do Sistema.
          </p>
          <p className="text-sm text-gray-500">
            API atual: <strong className="text-white">{globalConfig.api_type?.toUpperCase() || 'Não configurada'}</strong>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Gerenciar UazAPI por Loja</h2>
          <p className="text-gray-400 text-sm mt-1">
            Vincule instâncias UazAPI criadas no painel uazapi.dev a cada loja
          </p>
        </div>
        
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">Total:</span>
          <span className="text-white font-bold">{restaurants.length} lojas</span>
          <span className="text-gray-600">|</span>
          <span className="text-green-400">{restaurants.filter(r => r.has_uazapi_token).length} com UazAPI</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por nome, subdomínio ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
          />
        </div>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
        >
          <option value="all">Todos os status</option>
          <option value="connected">Conectados</option>
          <option value="disconnected">Desconectados</option>
          <option value="not_configured">Não configurados</option>
        </select>
      </div>

      {/* Instruções */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <h4 className="font-medium text-blue-400 mb-2">📋 Como configurar:</h4>
        <ol className="text-sm text-gray-400 space-y-1 list-decimal list-inside">
          <li>Crie uma instância no painel <a href="https://uazapi.dev" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">uazapi.dev</a></li>
          <li>Clique em &quot;Configurar&quot; na loja desejada abaixo</li>
          <li>Copie a <strong className="text-white">URL do Webhook</strong> e cole no painel UazAPI → Webhooks → URL</li>
          <li>No painel UazAPI, ative: <code className="bg-gray-800 px-1 rounded">addUrlEvents</code>, <code className="bg-gray-800 px-1 rounded">addUrlTypesMessages</code>, eventos: <code className="bg-gray-800 px-1 rounded">messages</code></li>
          <li>Cole o <strong className="text-white">Token da Instância</strong> no campo do Pede é Fácil e salve</li>
        </ol>
      </div>

      {/* Lista de Lojas */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Loja</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status WhatsApp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Instância</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredRestaurants.map((restaurant) => (
                <tr key={restaurant.id} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-white">{restaurant.name}</p>
                      <p className="text-xs text-gray-500">{restaurant.subdomain} • {restaurant.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(restaurant)}
                  </td>
                  <td className="px-4 py-3">
                    {restaurant.has_uazapi_token ? (
                      <span className="text-sm text-gray-300">{restaurant.instance_name || 'Configurado'}</span>
                    ) : (
                      <span className="text-sm text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {restaurant.has_uazapi_token ? (
                        <>
                          <button
                            onClick={() => handleOpenTestMessage(restaurant)}
                            className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                            title="Enviar mensagem de teste"
                          >
                            <FileText size={16} />
                          </button>
                          <button
                            onClick={() => handleOpenConfig(restaurant)}
                            className="p-2 text-orange-400 hover:bg-orange-500/20 rounded-lg transition-colors"
                            title="Reconfigurar"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleUnlink(restaurant)}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Desvincular"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleOpenConfig(restaurant)}
                          className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg transition-colors flex items-center gap-1"
                        >
                          <Key size={14} />
                          Configurar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRestaurants.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            Nenhuma loja encontrada com os filtros atuais
          </div>
        )}
      </div>

      {/* Modal de Configuração */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="bg-gray-900 border border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="text-orange-400" size={20} />
              Configurar UazAPI - {selectedRestaurant?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* URL do Webhook Individual */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
              <label className="block text-sm font-medium text-blue-400 mb-2">
                🔗 URL do Webhook (copie para o painel UazAPI)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`https://pedeefacil.com.br/api/webhook/uazapi/${selectedRestaurant?.id || ''}`}
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`https://pedeefacil.com.br/api/webhook/uazapi/${selectedRestaurant?.id || ''}`);
                    toast.success('URL copiada!');
                  }}
                  className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  title="Copiar URL"
                >
                  <Copy size={16} />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Cole esta URL no campo &quot;URL&quot; do webhook da instância no painel uazapi.dev
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Token da Instância *
              </label>
              <input
                type="text"
                value={instanceToken}
                onChange={(e) => setInstanceToken(e.target.value)}
                placeholder="Cole o token da instância aqui..."
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Copie do painel uazapi.dev → Sua Instância → Token
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nome da Instância (opcional)
              </label>
              <input
                type="text"
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
                placeholder="Ex: loja_centro"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Botão Testar Conexão */}
            <button
              onClick={handleTestConnection}
              disabled={testing || !instanceToken.trim()}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {testing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Testando...
                </>
              ) : (
                <>
                  <CheckCircle size={16} />
                  Testar Conexão
                </>
              )}
            </button>

            {/* Resultado do Teste */}
            {testResult && (
              <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                <p className={`text-sm ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                  {testResult.success ? '✅' : '❌'} {testResult.message}
                </p>
                {testResult.status && (
                  <p className="text-xs text-gray-400 mt-1">
                    Status: {testResult.status}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-700">
            <button
              onClick={() => setShowConfigModal(false)}
              className="flex-1 px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveConfig}
              disabled={saving || !instanceToken.trim()}
              className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Salvar
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Teste de Mensagem */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="bg-gray-900 border border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-blue-400" size={20} />
              Enviar Mensagem de Teste
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-400">
              Loja: <strong className="text-white">{selectedRestaurant?.name}</strong>
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Número do WhatsApp *
              </label>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="5538999999999"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Mensagem
              </label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-700">
            <button
              onClick={() => setShowTestModal(false)}
              className="flex-1 px-4 py-2 text-gray-400 hover:text-white border border-gray-700 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSendTestMessage}
              disabled={sendingTest || !testPhone.trim()}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sendingTest ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <FileText size={16} />
                  Enviar
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Re-export ProspectionModule
